import { Component } from '@theme/component';

/**
 * A segmented tab control that shows one collection grid at a time.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} tab
 * @property {HTMLElement[]} panel
 * @property {HTMLElement} tabsTrack
 *
 * @extends {Component<Refs>}
 */
class CategoryTabs extends Component {
  requiredRefs = ['tab', 'panel'];

  connectedCallback() {
    super.connectedCallback();

    // Each tab block renders its button immediately followed by its own
    // panel (see the section's markup), so in the DOM they start out
    // interleaved. Moving just the buttons into their own track element
    // lets that track scroll horizontally on mobile without dragging the
    // panels along with it - CSS alone can't decouple the two since they'd
    // otherwise have to share one flex/grid container's overflow.
    const { tab: tabs, tabsTrack } = this.refs;
    if (tabsTrack && tabs?.length) tabsTrack.append(...tabs);

    this.selectTab({ index: 0 });
  }

  /**
   * Activates the tab/panel at the given index.
   *
   * @param {{ index: number }} data
   */
  selectTab({ index }) {
    const { tab: tabs, panel: panels } = this.refs;

    tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('shop-by-category__tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    // On mobile the track scrolls horizontally, so the newly-selected tab
    // may be partially offscreen (or the reason it was tapped at all).
    // Centering it brings it fully into view and reveals a neighboring tab
    // on each side, so the slider stays discoverable as you click through it.
    tabs[index]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

    panels.forEach((panel, i) => {
      const isActive = i === index;
      // Re-adding the class each time (rather than leaving it on permanently)
      // matters because the entrance keyframes are also retriggered by the
      // display:none -> block flip below, so the panel fades/slides in fresh
      // on every switch instead of only the first time it's shown.
      panel.classList.toggle('shop-by-category__panel--entering', isActive);
      panel.style.display = isActive ? 'block' : 'none';
    });
  }

  /**
   * Activates the clicked tab. The button doesn't carry its own index (it's
   * rendered by its own block's template, which has no reliable way to know
   * its position among sibling blocks), so the index is derived here from
   * its position in `refs.tab` instead.
   *
   * @param {Event} event
   */
  onTabClick(event) {
    const { tab: tabs } = this.refs;
    const index = tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (index === -1) return;

    this.selectTab({ index });
  }

  /**
   * Arrow-key navigation between tabs, per the ARIA tabs pattern.
   *
   * @param {KeyboardEvent} event
   */
  onKeyDown(event) {
    const { key } = event;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(key)) return;

    const { tab: tabs } = this.refs;
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (current === -1) return;

    event.preventDefault();

    let next = current;
    if (key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (key === 'Home') next = 0;
    if (key === 'End') next = tabs.length - 1;

    this.selectTab({ index: next });
    tabs[next].focus();
  }
}

if (!customElements.get('category-tabs-component')) {
  customElements.define('category-tabs-component', CategoryTabs);
}
